import { combineEpics } from 'redux-observable';
import { of } from 'rxjs';
import { LOCATION_CHANGE } from 'connected-next-router';
import {
	SET_DEVICE_TYPE,
	SET_LANGUAGE,
	SIDE_MENU_HIDE,
	SIDE_MENU_TOGGLE,
} from '@legacyApp/client/store/app/app.constants';
import { config } from '@legacyApp/client/config';
import { USER_LOGOUT } from '@legacyApp/client/store/user/user.constants';
import { alertsActions } from '@modules/alerts/store/alerts.actions';
import { trans } from '@legacyApp/client/modules/translation/translate';
import { regexStrings } from '@legacyApp/client/modules/app/regexService';
import { epic, epicOldState } from '@legacyApp/client/modules/app/epicService';
import { setLockedView } from '@legacyApp/client/store/app/app.actions';
import { Sounds } from '@common/constants/config/sounds';
import { regexString } from '@legacyApp/client/modules/app/regexString';
import { REGISTRY_CHAT_PACKAGE } from '@legacyApp/client/store/registry/registry.constants';
import { getPathnameWithoutLang } from '@common/methods/getPathnameWithoutLang/getPathnameWithoutLang';
import { ALERT_TYPES } from '@modules/alerts/store/@types/AlertType';
import { sportsBetSlipClearPlacedBets } from '../../sports/store/sportsBetSlip/sportsBetSlip.thunk';
import { toggleChatVisibilityBodyClass } from '../methods/toggleChatVisibilityBodyClass';
import { getChatVisibility } from '../methods/getChatVisibility';
import { setChatVisibility } from '../methods/setChatVisibility';
import { checkRealUserName } from '../methods/checkRealUserName';
import { checkChatDisabled } from '../methods/checkChatDisabled';
import audioService from '../../../LegacyApp/client/modules/app/audioService';
import { ChatBotTypes, createBotLogin } from '../constants/BOTS';
import { chatActions } from './chat.actions';
import { unbindStreams } from './chat.constants';
import { unbindChatStreamActionThunk } from './thunks/unbindChatStreamAction.thunk';
import { getRoomStreamThunk } from './thunks/getRoomStream.thunk';

const { showAlert } = alertsActions;

const visibilityEpic = epic<ReturnType<typeof chatActions.setVisible>>(
	'visibilityEpic',
	{
		actions: (ofType) => ofType(chatActions.setVisible().type),
		callback: ({ action, store$ }) => {
			const actions = [];
			const { isVisible, noLs } = action.payload;
			if ((isVisible && store$.value.app.mobile) || !isVisible) {
				actions.push(setLockedView('visibilityEpic', !!isVisible));
			}
			if (!noLs) {
				setChatVisibility(isVisible);
			}
			toggleChatVisibilityBodyClass(isVisible);
			if (!isVisible) {
				actions.push(sportsBetSlipClearPlacedBets());
			}
			return of(actions);
		},
	},
);

const switchRoomEpic = epic<ReturnType<typeof chatActions.setRoom>>(
	'switchRoomEpic',
	{
		actions: (ofType) => ofType(chatActions.setRoom().type),
		callback: ({ action, store$ }) => {
			const result = [];
			if (action.payload.lastRoom) {
				result.push(unbindChatStreamActionThunk(action.payload.lastRoom));
			}
			unbindStreams.reset(store$.value?.chat?.room);
			if (!store$.value?.chat?.stream[action.payload.name]) {
				return of([
					...result,
					chatActions.setStream({ room: action.payload.name, list: [] }),
					getRoomStreamThunk(),
				]);
			}
			if (!store$.value?.chat?.stream?.[action.payload.name].length) {
				return of([...result, getRoomStreamThunk()]);
			}
			return of(result);
		},
	},
);

const updateStreamEpic = epic<ReturnType<typeof chatActions.updateStream>>(
	'updateStreamEpic',
	{
		actions: (ofType) => ofType(chatActions.updateStream().type),
		callback: ({ action, store$ }) => {
			const regexResult = regexString(
				regexStrings.userName(store$.value.user.name),
				action.payload.data.message,
			);
			// console.log('test regex', {
			//   regexResult: regexResult,
			//   message: action.payload.message,
			// });
			if (
				store$.value.user.name &&
				!!regexResult &&
				action.payload.data.message.indexOf(
					`goes from @${store$.value.user.name}`,
				) === -1
			) {
				if (
					action.payload.data.user.login === createBotLogin(ChatBotTypes.RAIN)
				) {
					const split = action.payload.data.message.split(' ');
					const amount = parseFloat(split[0]);
					const currency = split[1].toLowerCase();
					return of(
						showAlert({
							type: ALERT_TYPES.INFO,
							message: trans({
								label: 'You have received {{amount}} {{currency}} from RAIN',
								options: {
									amount,
									currency: currency.toUpperCase(),
								},
							}),
						}),
					);
				}
				if (
					action.payload.data.user.login === createBotLogin(ChatBotTypes.TIP)
				) {
					if (
						action.payload.data.message.indexOf(
							`@${store$.value.user.name}`,
						) === 0
					) {
						return of();
					}
					const split = action.payload.data.message.split(' ');
					const user = split[0].replace('@', '').split(':')[0];
					const amount = parseFloat(split[2]);
					const currency = split[3].toLowerCase();
					return of(
						showAlert({
							type: ALERT_TYPES.INFO,
							message: trans({
								label:
									'You have received {{amount}} {{currency}} TIP from {{user}}',
								options: {
									amount,
									currency: currency.toUpperCase(),
									user,
								},
							}),
						}),
					);
				}
				// TODO: chat store usage
				if (
					action.payload.data.user.login !== store$.value.user.name &&
					action.payload.room === store$.value?.chat?.room
				) {
					audioService.play(Sounds.mention, undefined, 0.2);
					return of(
						showAlert({
							type: ALERT_TYPES.INFO,
							message: trans({
								label: 'You were mentioned by {{user}}',
								options: {
									user:
										typeof action.payload.data.user.login === 'string'
											? action.payload.data.user.login
											: '',
								},
							}),
							noTimeout: config.mentionedAlertTimeout,
						}),
					);
				}
			}
			// TODO: chat store usage
			if (
				store$.value?.chat?.users.indexOf(action.payload.data.user.login) >
					-1 ||
				!checkRealUserName(action.payload.data.user.login)
			) {
				return of();
			}
			return of(chatActions.addUser(action.payload.data.user.login));
		},
	},
);

const initializedEpic = epic('initializedEpic', {
	actions: (ofType) => ofType(REGISTRY_CHAT_PACKAGE),
	callback: ({ store$ }) => {
		if (store$.value.chat.room) {
			return of();
		}
		const room =
			config.chat.rooms.find((el) => store$.value.app.language === el.symbol)
				?.id || config.chat.rooms[0].id;
		if (room) {
			return of(chatActions.setRoom({ name: room }));
		}
		return of();
	},
});

const widthEpic = epic('widthEpic', {
	actions: (ofType) =>
		ofType(
			SET_DEVICE_TYPE,
			chatActions.setVisible().type,
			SIDE_MENU_HIDE,
			SIDE_MENU_TOGGLE,
		),
	callback: ({ store$ }) => {
		// TODO: chat store usage
		const width =
			!store$.value?.chat?.visible && !store$.value?.app?.isSideMenuVisible
				? 0
				: store$.value.app.deviceType === 'desktopBig'
				? 370
				: 320;
		// TODO: chat store usage
		if (width !== store$.value?.chat?.width) {
			return of(chatActions.setWidth(width));
		}
		return of();
	},
});

const contentTypeChangeEpic = epic<
	ReturnType<typeof chatActions.setContentType>
>('contentTypeChangeEpic', {
	actions: (ofType) => ofType(chatActions.setContentType().type),
	callback: () => {
		return of(chatActions.setPrivateActive());
	},
});

const logoutEpic = epic('logoutEpic', {
	actions: (ofType) => ofType(USER_LOGOUT),
	callback: () => {
		return of(chatActions.setContentType());
	},
});

const disableEpic = epicOldState('disableEpic', {
	actions: (ofType) => ofType(LOCATION_CHANGE, SET_LANGUAGE),
	callback: ({ store$, newState, oldState }) => {
		const { mobile, language } = store$.value.app;
		// TODO: chat store usage
		const { disabled, visible } = store$.value?.chat || {};

		const newPathname = getPathnameWithoutLang(
			newState.router.location.pathname,
		);
		const oldPathname = getPathnameWithoutLang(
			oldState.router.location.pathname,
		);

		if (!language) {
			return [];
		}
		const isDisabled = checkChatDisabled(newPathname);
		const result = [];
		if (disabled !== isDisabled) {
			result.push(chatActions.setDisabled(isDisabled));
		}
		if (isDisabled && visible) {
			result.push(chatActions.setVisible({ isVisible: false, noLs: true }));
		}
		if (!isDisabled) {
			if (mobile && visible) {
				if (oldPathname === newPathname) {
					return result;
				}
				return [...result, chatActions.setVisible({ isVisible: false })];
			}
			const bool = getChatVisibility();
			if (!mobile && bool !== visible) {
				result.push(chatActions.setVisible({ isVisible: bool }));
			}
		}
		return result;
	},
});

const chatEpic = combineEpics(
	switchRoomEpic,
	visibilityEpic,
	updateStreamEpic,
	initializedEpic,
	widthEpic,
	contentTypeChangeEpic,
	logoutEpic,
	disableEpic,
);

export default chatEpic;
